/**
 * custom-accessibility.js
 * 
 * 核心的无障碍修复脚本。
 * 该脚本使用了高度解耦的模块化框架，通过统一的 MutationObserver 捕获 DOM 变化，
 * 然后分发给注册的具体插件修复模块执行，从而解决第三方 App 带来的无障碍扫描报错问题。
 */

document.addEventListener('DOMContentLoaded', () => {
    // =====================================================================
    // 第一部分：注册器与插件解耦架构
    // =====================================================================
    const accessibilityFixers = [];

    // 暴露全局注册方法，其他 JS 文件也可随时调用并注册自己的修复逻辑
    window.registerAccessibilityFixer = (fixerFn) => {
        if (typeof fixerFn === 'function') {
            accessibilityFixers.push(fixerFn);
        }
    };

    // =====================================================================
    // 第二部分：各类插件的专项修复逻辑
    // 未来如果由于某个插件需要卸载或修改，只需处理这部分即可，互不影响。
    // =====================================================================

    /**
     * 针对 Rivo 插件的无障碍修复
     * 为了提升性能和避免误伤主题的合法代码，我们仅在 Rivo 插件的根容器 `#rivo-account-slideout` 内执行查询。
     */
    window.registerAccessibilityFixer(function fixRivoApp() {
        const rivoContainer = document.getElementById('rivo-account-slideout');
        if (!rivoContainer) return; // 如果该组件还没注入到页面，直接返回，节约性能

        // 1. 修复空标题 (Empty heading) 问题
        // 由于没有配置内容，这里会一直是一个空的 <div role="heading">
        // 因此直接将其对无障碍阅读器的可见性干掉（视觉上也隐藏）
        const rivoTitles = rivoContainer.querySelectorAll('.rivo-aw-title[role="heading"]');
        rivoTitles.forEach(title => {
            if (title.textContent.trim() === '') {
                title.removeAttribute('role');
                title.removeAttribute('aria-level');
                title.setAttribute('aria-hidden', 'true');
                title.style.display = 'none';
            }
        });

        // 2. 修复空链接 (Empty link) 问题
        // Rivo 插件生成的 href="" 的空 <a> 标签，直接移除其 href 属性使其失去链接语义
        const rivoLinks = rivoContainer.querySelectorAll('a[rivo-ax-bind\\:href]');
        rivoLinks.forEach(link => {
            if (link.textContent.trim() === '' && (!link.hasAttribute('href') || link.getAttribute('href') === '' || link.getAttribute('href') === '#')) {
                link.removeAttribute('href');
                link.setAttribute('aria-hidden', 'true');
                link.style.display = 'none';
            }
        });

        // 3. 查找有 rivo 绑定但 alt 为空或缺失的图片
        const rivoImages = rivoContainer.querySelectorAll('img[rivo-ax-bind\\:alt], img[src*="rivo"]');
        rivoImages.forEach(img => {
            if ((!img.hasAttribute('alt') || img.getAttribute('alt') === '') && img.getAttribute('src') !== '') {
                img.setAttribute('alt', 'Featured reward content');
            }
        });
    });

    // =====================================================================
    // [扩展位]：如果未来有其他插件报错，就在这里注册一个新的修复器
    // =====================================================================

    /**
     * 针对 Optimonk 插件的无障碍修复
     * 解决 Missing alternative text (图片缺失 alt)
     * 和 Missing form label (表单输入框缺失 aria-label)
     */
    window.registerAccessibilityFixer(function fixOptimonkApp() {
        // 在所有以 om-holder-campaign- 开头的容器内查找（Optimonk 的弹窗或嵌入表单）
        const optimonkHolders = document.querySelectorAll('.om-holder[id^="om-holder-campaign-"]');
        if (!optimonkHolders || optimonkHolders.length === 0) return;

        optimonkHolders.forEach(holder => {
            // 1. 修复缺少 alt 的图片
            const omImages = holder.querySelectorAll('img.om-image');
            omImages.forEach(img => {
                if (!img.hasAttribute('alt')) {
                    img.setAttribute('alt', 'Optimonk Promotional Image'); // 避免死循环，缺失时才加
                }
            });

            // 2. 修复缺少 label 的 input/textarea
            // 如果 input 自身没有关联的 <label> 或者没有 aria-label 属性，补齐 aria-label
            const omInputs = holder.querySelectorAll('input.om-inputs, textarea.om-inputs');
            omInputs.forEach(input => {
                if (!input.hasAttribute('aria-label')) {
                    // 使用 placeholder 的值作为 aria-label，如果没有则默认一个文本
                    const placeholderText = input.getAttribute('placeholder');
                    input.setAttribute('aria-label', placeholderText || 'Optimonk Input Field');
                }
            });
        });
    });

    window.registerAccessibilityFixer(function fixLanternTrackerApp() {
        // 修复 Lantern Roeye 追踪脚本的隐形图片缺少 alt 属性的问题
        // 使用更精确的选择器，避免性能问题
        const lanternImages = document.querySelectorAll('img[src^="https://lantern.roeye.com/track.php"]');
        // 由于 MutationObserver 每次触发都会跑这个循环，如果一直 setAttribute 可能会导致无限循环或性能问题，
        // 我们只在确实没有相关属性时才处理
        lanternImages.forEach(img => {
            if (!img.hasAttribute('alt')) {
                img.setAttribute('alt', ''); // 设置为空 alt 以标记为装饰性/追踪用途图片
            }
            if (!img.hasAttribute('aria-hidden')) {
                img.setAttribute('aria-hidden', 'true'); // 告诉屏幕阅读器忽略此元素
            }
        });
    });

    window.registerAccessibilityFixer(function fixTrafficCdnTrackerApp() {
        // 修复 TrafficCdn 追踪脚本的隐形图片缺少 alt 属性的问题
        const trafficCdnImages = document.querySelectorAll('img[src^="https://trafficcdn.com/"]');
        trafficCdnImages.forEach(img => {
            if (!img.hasAttribute('alt')) {
                img.setAttribute('alt', '');
            }
            if (!img.hasAttribute('aria-hidden')) {
                img.setAttribute('aria-hidden', 'true');
            }
        });
    });

    window.registerAccessibilityFixer(function fixJudgeMeApp() {
        // 修复 Judge.me (jdgm) 插件生成的评论区域包含空链接 (Empty link) 的问题
        // 该组件仅在 product 页面使用，只有存在 .product 容器或特定 class 时才执行修复
        if (!document.querySelector('body.template-product #judgeme_product_reviews')) return;

        const jdgmBars = document.querySelectorAll('.jdgm-rev__scale-range .jdgm-cf-bar');
        jdgmBars.forEach(bar => {
            if (!bar.hasAttribute('aria-label') && bar.textContent.trim() === '') {
                // 如果该元素既没有 aria-label 也没有文本，就为屏幕阅读器添加文本说明
                bar.setAttribute('aria-label', 'Review rating scale');
            }
        });
    });

    /*
    window.registerAccessibilityFixer(function fixOtherApp() {
        // 其他插件如 Yotpo, Klaviyo 等的空 alt/aria-label 修复代码...
    });
    */


    // =====================================================================
    // 第三部分：核心 DOM 监听器（统一管理，保障页面性能）
    // =====================================================================
    const targetNode = document.body;
    // 监听子节点的增删及相关无障碍属性的变化
    const config = { childList: true, subtree: true, attributes: true, characterData: true, attributeFilter: ['alt', 'src', 'role', 'href'] };

    const coreObserverCallback = (mutationsList) => {
        // 如果有符合要求的变化，才触发所有的 Fixers
        const hasRelevantMutations = mutationsList.some(m => ['childList', 'attributes', 'characterData'].includes(m.type));
        if (!hasRelevantMutations) return;

        // 遍历执行所有已解耦的第三方修复逻辑
        accessibilityFixers.forEach(fixer => {
            try {
                fixer();
            } catch (err) {
                console.warn('Accessibility Fixer Error:', err);
            }
        });
    };

    // 初始先执行一次，修复页面上已经存在的报错元素
    accessibilityFixers.forEach(fixer => {
        try {
            fixer();
        } catch (err) { }
    });

    const observer = new MutationObserver(coreObserverCallback);
    observer.observe(targetNode, config);

    // 兜底机制：15秒后自动断开监听，防止一直挂载占用主线程资源（影响 INP/响应性）
    // 大部分第三方弹窗及组件都在 5-10 秒内加载渲染完毕
    setTimeout(() => {
        observer.disconnect();
        console.log('Core Accessibility observer disconnected to save performance.');
    }, 15000);
});
